import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    TextField,
    Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Header from '../Header';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';
import { AVAILABLE_AVATARS } from '../../config/avatars';

/**
 * Full-page profile screen (not a modal) — a Dialog's own scroll container
 * used to fight the page's scroll on mobile whenever a touch started over
 * the avatar grid, so the whole avatar list scrolled the emoji grid instead
 * of the page. A regular page has just one scroll container, so that
 * conflict can't happen.
 */
export default function OnlineProfileScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, authFetch, updateUser, logout, logoutAllDevices } = useAuth();

    const [name, setName] = useState(user?.name || '');
    const [avatar, setAvatar] = useState(user?.avatar || '');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [isLoggingOutAll, setIsLoggingOutAll] = useState(false);
    const [isSendingVerification, setIsSendingVerification] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);

    const requiresPasswordToDelete = user?.provider === 'LOCAL';

    useEffect(() => {
        if (user) {
            setName(user.name || '');
            setAvatar(user.avatar || '');
        }
    }, [user]);

    const handleSave = async () => {
        if (!name.trim()) return;
        setError(null);
        setIsSaving(true);
        try {
            const updated = await authFetch('/users/me', {
                method: 'PATCH',
                body: { name: name.trim(), avatar: avatar || undefined },
            });
            updateUser(updated);
            navigate('/online');
        } catch (err) {
            setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined)));
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/online');
    };

    const handleDeleteAccount = async () => {
        setError(null);
        setIsDeleting(true);
        try {
            await authFetch('/users/me', {
                method: 'DELETE',
                body: requiresPasswordToDelete ? { password: deletePassword } : undefined,
            });
            setDeleteConfirmOpen(false);
            await logout();
            navigate('/');
        } catch (err) {
            // Wrong/missing password: stay in the dialog so the user can retry.
            const code = err instanceof ApiError ? err.code : undefined;
            if (code !== 'INVALID_PASSWORD') setDeleteConfirmOpen(false);
            setError(t(getErrorMessageKey(code)));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleLogoutAllDevices = async () => {
        setError(null);
        setIsLoggingOutAll(true);
        try {
            await logoutAllDevices();
            navigate('/online');
        } catch (err) {
            setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined)));
        } finally {
            setIsLoggingOutAll(false);
        }
    };

    const handleResendVerification = async () => {
        setError(null);
        setIsSendingVerification(true);
        try {
            await authFetch('/auth/verify-email/request', { method: 'POST' });
            setVerificationSent(true);
        } catch (err) {
            setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined)));
        } finally {
            setIsSendingVerification(false);
        }
    };

    return (
        <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flexShrink: 0 }}>
                <Header title={t('online.profile.title')} onBack={() => navigate('/online')} showSettings={false} />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            <Container maxWidth="sm" sx={{ mt: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {user && !user.emailVerified && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        action={
                            !verificationSent && (
                                <Button color="inherit" size="small" disabled={isSendingVerification} onClick={handleResendVerification}>
                                    {t('online.profile.verify_email_button')}
                                </Button>
                            )
                        }
                    >
                        {verificationSent ? t('online.profile.verify_email_sent') : t('online.profile.verify_email_message')}
                    </Alert>
                )}

                <Box sx={{
                    display: 'flex',
                    gap: { xs: 2, sm: 3 },
                    alignItems: 'flex-start',
                    '@media (orientation: portrait)': { flexDirection: 'column' }
                }}>
                    {/* Left column: name and avatar picker */}
                    <Box sx={{ flex: '2 1 0%', minWidth: 0, '@media (orientation: portrait)': { flex: 'none', width: '100%' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ width: 56, height: 56, fontSize: '1.8rem', bgcolor: 'rgba(102, 126, 234, 0.15)', flexShrink: 0 }}>
                                {avatar}
                            </Avatar>
                            <TextField
                                label={t('online.profile.name_label')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                inputProps={{ maxLength: 20 }}
                            />
                        </Box>

                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            {t('online.profile.avatar_label')}
                        </Typography>
                        <Card
                            variant="outlined"
                            sx={{
                                mb: 2,
                                p: 1,
                                maxHeight: { xs: 130, sm: 160 },
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {AVAILABLE_AVATARS.map((emoji, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => setAvatar(emoji)}
                                        sx={{
                                            fontSize: '1.6rem',
                                            cursor: 'pointer',
                                            width: 40,
                                            height: 40,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            transition: 'transform 0.2s',
                                            bgcolor: avatar === emoji ? 'rgba(102, 126, 234, 0.25)' : 'transparent',
                                            border: avatar === emoji ? '2px solid #667eea' : '2px solid transparent',
                                            '&:hover': { transform: 'scale(1.15)', bgcolor: 'rgba(102, 126, 234, 0.15)' },
                                        }}
                                    >
                                        {emoji}
                                    </Box>
                                ))}
                            </Box>
                        </Card>

                        <Button variant="contained" fullWidth disabled={isSaving || !name.trim()} onClick={handleSave}>
                            {t('online.profile.save_button')}
                        </Button>
                    </Box>

                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ display: 'none', '@media (orientation: landscape)': { display: 'block' } }}
                    />
                    <Divider sx={{ display: 'none', width: '100%', my: 0.5, '@media (orientation: portrait)': { display: 'block' } }} />

                    {/* Right column: session/account actions */}
                    <Box sx={{
                        flex: '1 1 0%',
                        minWidth: { xs: 110, sm: 140 },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        '@media (orientation: portrait)': { flex: 'none', minWidth: 0, width: '100%' }
                    }}>
                        {user?.role === 'ADMIN' && (
                            <>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    startIcon={<AdminPanelSettingsIcon />}
                                    onClick={() => navigate('/admin')}
                                >
                                    Panel de administración
                                </Button>
                                <Divider sx={{ my: 0.5 }} />
                            </>
                        )}
                        <Button variant="outlined" color="error" onClick={handleLogout}>
                            {t('online.lobby.logout')}
                        </Button>
                        <Button
                            variant="text"
                            color="error"
                            size="small"
                            disabled={isLoggingOutAll}
                            onClick={handleLogoutAllDevices}
                        >
                            {t('online.profile.logout_all_button')}
                        </Button>

                        <Divider sx={{ my: 0.5 }} />

                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteForeverIcon />}
                            onClick={() => {
                                setDeletePassword('');
                                setDeleteConfirmOpen(true);
                            }}
                            sx={{
                                borderWidth: 2,
                                fontWeight: 'bold',
                                '&:hover': { borderWidth: 2 },
                            }}
                        >
                            {t('online.profile.delete_account_button')}
                        </Button>
                    </Box>
                </Box>
            </Container>
            </Box>

            <Dialog open={deleteConfirmOpen} onClose={() => (isDeleting ? null : setDeleteConfirmOpen(false))}>
                <DialogTitle>{t('online.profile.delete_confirm_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('online.profile.delete_confirm_message')}</DialogContentText>
                    {requiresPasswordToDelete && (
                        <TextField
                            label={t('online.profile.delete_confirm_password_label')}
                            type="password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            fullWidth
                            autoFocus
                            sx={{ mt: 2 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>
                        {t('online.profile.delete_confirm_cancel')}
                    </Button>
                    <Button
                        color="error"
                        onClick={handleDeleteAccount}
                        disabled={isDeleting || (requiresPasswordToDelete && !deletePassword)}
                    >
                        {t('online.profile.delete_confirm_confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
